import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ShieldAlert, Power, Activity, Ban, ShieldQuestion, Gauge, Inbox } from "lucide-react";
import api from "@/lib/api";
import Pager from "@/components/Pager";

function AbuseMonitor({ onBlocked }) {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/abuse/overview").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const blockIp = async (ip) => {
    try {
      await api.post("/admin/blocklist", { type: "ip", value: ip });
      toast.success(`Blocked ${ip}`);
      load();
      onBlocked?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!d) return null;
  const tiles = [
    { k: "Captcha challenges", v: d.captcha_failed_24h, icon: ShieldQuestion },
    { k: "Login gates", v: d.login_gate_24h, icon: Ban },
    { k: "Rate-limited", v: d.rate_limited_24h, icon: Gauge },
    { k: "Guest tickets", v: d.guest_tickets_24h, icon: Inbox },
    { k: "IPs blocked", v: d.blocked_ips, icon: ShieldAlert },
  ];
  return (
    <div className="card-moss mb-8 p-6" data-testid="abuse-monitor">
      <div className="mb-4 flex items-center gap-2">
        <Activity size={16} className="text-[color:var(--evergreen)]" />
        <p className="overline">Abuse monitor · last 24h</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {tiles.map((t) => (
          <div key={t.k} className="rounded-lg border border-[color:var(--ink-hairline)] bg-white p-4" data-testid={`abuse-tile-${t.k.toLowerCase().replace(/ /g, "-")}`}>
            <t.icon size={15} className="text-[color:var(--ink-muted)]" />
            <p className="mt-2 font-editorial text-3xl text-[color:var(--ink)]">{t.v}</p>
            <p className="text-xs text-[color:var(--ink-muted)]">{t.k}</p>
          </div>
        ))}
      </div>

      {d.top_ips?.length > 0 && (
        <div className="mt-6">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Top offending IPs · last 7 days</p>
          <div className="grid gap-1.5">
            {d.top_ips.map((r) => (
              <div key={r.ip} className="flex items-center justify-between rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm" data-testid={`abuse-ip-${r.ip}`}>
                <div className="min-w-0">
                  <span className="font-mono-edge text-[color:var(--ink)]">{r.ip}</span>
                  <span className="ml-2 text-xs text-[color:var(--ink-muted)]">{r.events} events · {r.captcha} captcha · {String(r.last).slice(0, 16).replace("T", " ")}</span>
                </div>
                {r.blocked ? (
                  <span className="chip chip-neutral">blocked</span>
                ) : (
                  <button onClick={() => blockIp(r.ip)} data-testid={`abuse-block-${r.ip}`} className="btn-edge text-xs"><Ban size={12} /> Block</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {d.recent?.length > 0 && (
        <details className="mt-5">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Recent events ({d.recent.length})</summary>
          <div className="mt-2 grid max-h-64 gap-1 overflow-y-auto" data-testid="abuse-recent">
            {d.recent.map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded border border-[color:var(--ink-hairline)] bg-white px-3 py-1.5 text-xs">
                <span><span className="chip chip-neutral mr-2">{e.type}</span><span className="font-mono-edge text-[color:var(--ink)]">{e.ip}</span> {e.detail && <span className="text-[color:var(--ink-muted)]">· {e.detail}</span>}</span>
                <span className="text-[color:var(--ink-muted)]">{String(e.created_at).slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function AdminBlocklist() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("email");
  const [value, setValue] = useState("");

  const load = () => api.get("/admin/blocklist").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const [dz, setDz] = useState({ enabled: false, count: 0 });
  const [q, setQ] = useState("");
  const [dzRows, setDzRows] = useState([]);
  const [dzBusy, setDzBusy] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const loadDz = () => api.get("/admin/disposable/status").then((r) => setDz(r.data)).catch(() => {});
  useEffect(() => { loadDz(); }, []);
  const [browse, setBrowse] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const loadBrowse = (page = 1) => api.get(`/admin/disposable/list?page=${page}&limit=20`).then((r) => setBrowse(r.data)).catch(() => {});
  useEffect(() => { loadBrowse(1); }, []);
  const searchDz = async (term) => {
    setQ(term);
    try { const { data } = await api.get(`/admin/disposable/search?q=${encodeURIComponent(term)}`); setDzRows(term.trim() ? data : []); }
    catch { /* silent */ }
  };
  const importDz = async () => {
    setDzBusy(true);
    try { const { data } = await api.post("/admin/disposable/import"); toast.success(`Imported ${data.imported} domains`); loadDz(); loadBrowse(1); }
    catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setDzBusy(false); }
  };
  const toggleDz = async () => {
    try { const { data } = await api.post("/admin/disposable/toggle", { enabled: !dz.enabled }); setDz({ ...dz, enabled: data.enabled }); toast.success(data.enabled ? "Guard enabled" : "Guard disabled"); }
    catch { toast.error("Failed"); }
  };
  const addDz = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    try { await api.post("/admin/disposable", { domain: newDomain.trim() }); toast.success("Domain added"); setNewDomain(""); loadDz(); loadBrowse(browse.page); if (q) searchDz(q); }
    catch { toast.error("Failed"); }
  };
  const removeDz = async (d) => { await api.delete(`/admin/disposable/${encodeURIComponent(d)}`); loadDz(); loadBrowse(browse.page); searchDz(q); };

  const add = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    try {
      await api.post("/admin/blocklist", { type, value: value.trim() });
      toast.success("Added to blocklist");
      setValue("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => { await api.delete(`/admin/blocklist/${id}`); load(); };

  return (
    <div data-testid="admin-blocklist">
      <div className="mb-6">
        <p className="overline mb-2">Admin · Anti-abuse</p>
        <h1 className="font-editorial text-3xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-4xl">Block list</h1>
        <p className="mt-2 max-w-2xl text-sm text-[color:var(--ink-muted)]">Block registration, login and contact by IP, email address, or email domain. Rate limiting and email throttling run automatically on all public forms.</p>
      </div>

      <AbuseMonitor onBlocked={load} />

      <div className="card-moss mb-8 p-6" data-testid="disposable-guard">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="overline mb-1">Disposable email guard</p>
            <p className="text-sm text-[color:var(--ink-muted)]">{Number(dz.count).toLocaleString()} throwaway domains loaded · guard is <strong className={dz.enabled ? "text-[color:var(--evergreen)]" : "text-[color:var(--alert)]"}>{dz.enabled ? "ON" : "OFF"}</strong></p>
          </div>
          <div className="flex gap-2">
            <button onClick={importDz} disabled={dzBusy} data-testid="disposable-import" className="btn-edge-ghost text-sm">{dzBusy ? "Importing…" : "Import / refresh list"}</button>
            <button onClick={toggleDz} data-testid="disposable-toggle" className="btn-edge text-sm"><Power size={14} /> {dz.enabled ? "Disable" : "Enable"}</button>
          </div>
        </div>
        <form onSubmit={addDz} className="mt-5 flex flex-wrap items-end gap-3">
          <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="add a domain e.g. tempmail.com" data-testid="disposable-add-input"
            className="flex-1 min-w-[220px] rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
          <button data-testid="disposable-add" className="btn-edge-ghost text-sm"><Plus size={14} /> Add</button>
        </form>
        <div className="mt-4">
          <input value={q} onChange={(e) => searchDz(e.target.value)} placeholder="search loaded domains…" data-testid="disposable-search"
            className="w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
          {dzRows.length > 0 && (
            <div className="mt-2 grid max-h-64 gap-1 overflow-y-auto">
              {dzRows.map((r) => (
                <div key={r.domain} className="flex items-center justify-between rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-1.5 text-sm text-[color:var(--ink)]" data-testid={`disposable-row-${r.domain}`}>
                  {r.domain}
                  <button onClick={() => removeDz(r.domain)} className="text-[color:var(--alert)] hover:opacity-70"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5" data-testid="disposable-browse">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">All loaded domains</p>
          {browse.items.length === 0 ? (
            <p className="rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-3 text-sm text-[color:var(--ink-muted)]">No domains loaded yet. Click “Import / refresh list”.</p>
          ) : (
            <>
              <div className="grid gap-1 sm:grid-cols-2">
                {browse.items.map((r) => (
                  <div key={r.domain} className="flex items-center justify-between rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-1.5 text-sm text-[color:var(--ink)]" data-testid={`disposable-browse-${r.domain}`}>
                    {r.domain}
                    <button onClick={() => removeDz(r.domain)} className="text-[color:var(--alert)] hover:opacity-70"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
              <Pager page={browse.page} pages={browse.pages} total={browse.total} onPage={(p) => loadBrowse(p)} testId="disposable-browse-pager" />
            </>
          )}
        </div>
      </div>

      <form onSubmit={add} className="mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Type</label>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="blocklist-type"
            className="rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)]">
            <option value="email">email</option>
            <option value="domain">domain</option>
            <option value="ip">ip</option>
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Value</label>
          <input value={value} onChange={(e) => setValue(e.target.value)} data-testid="blocklist-value" placeholder="spammer@bad.com / bad.com / 1.2.3.4"
            className="w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none" />
        </div>
        <button data-testid="blocklist-add" className="btn-edge text-sm"><Plus size={14} /> Block</button>
      </form>

      <div className="grid gap-2">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between rounded-md border border-[color:var(--ink-hairline)] bg-white p-4">
            <div className="flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-md bg-[color:var(--flare-soft)] text-[color:var(--flare-ink)]"><ShieldAlert size={15} /></span>
              <div><span className="chip chip-neutral mr-2">{r.type}</span><span className="text-sm text-[color:var(--ink)]">{r.value}</span></div>
            </div>
            <button onClick={() => remove(r.id)} data-testid={`blocklist-remove-${r.id}`} className="rounded-md border border-[color:var(--ink-hairline)] p-2 text-[color:var(--alert)] hover:border-[color:var(--alert)]"><Trash2 size={14} /></button>
          </div>
        ))}
        {rows.length === 0 && <div className="card-edge p-8 text-[color:var(--ink-muted)]">Nothing blocked. The site is open.</div>}
      </div>
    </div>
  );
}
